import { API, graphqlOperation } from "aws-amplify";
import { searchProductLibProducts } from "../graphql/queries";
import { updateProductLibProduct } from "../graphql/mutations";
import { ProductLibProduct } from "../API";

// materialCode condition for one search term: exact match on the whole code as a
// string. matchPhrase matches the exact token sequence, so the hyphen only acts
// as a separator (it isn't matched literally) — "011-0357" matches the code
// "011-0357" exactly, not partial/prefix.
const matcodeCondition = (term: string): any => ({
  materialCode: { matchPhrase: term.toLowerCase() },
});

export type ProductSortField =
  | "materialCode"
  | "name"
  | "updatedAt"
  | "packageCaseUpdatedAt";

export interface SearchProductsParams {
  brand?: string; // "" / undefined = all brands
  search?: string; // token search over matCode + name
  page: number; // 1-based
  pageSize: number;
  sortField: ProductSortField;
  sortDirection: "asc" | "desc";
}

export interface SearchProductsResult {
  items: ProductLibProduct[];
  total: number;
}

// OpenSearch default index.max_result_window — from + size must stay within it.
export const SEARCH_WINDOW = 10000;

// Builds the OpenSearch filter shared by search + count: packaging fixed to EA,
// optional brand, and per term a wildcard match over materialCode (see
// matcodeCondition) OR a prefix match over name.
const buildProductFilter = (brand?: string, search?: string): any => {
  const and: any[] = [{ packaging: { match: "EA" } }];
  if (brand) and.push({ brand: { match: brand } });

  const terms = (search ?? "").trim().split(/\s+/).filter(Boolean);
  for (const t of terms) {
    and.push({
      or: [matcodeCondition(t), { name: { matchPhrasePrefix: t } }],
    });
  }
  return { and };
};

// Returns just the total count for a brand/search filter (no records fetched).
export const countProducts = async (params: {
  brand?: string;
  search?: string;
}): Promise<number> => {
  const result: any = await API.graphql(
    graphqlOperation(searchProductLibProducts, {
      filter: buildProductFilter(params.brand, params.search),
      limit: 1,
    })
  );
  return result?.data?.searchProductLibProducts?.total ?? 0;
};

// Server-side product search: packaging fixed to EA, optional single brand,
// token search over materialCode + name, server sort + cursor pagination.
// Uses `from` (offset) pagination — deterministic per page. Capped to the
// OpenSearch 10,000 (index.max_result_window) window; a secondary id sort keeps
// results stable across pages when the primary field has ties/nulls.
export const searchProducts = async (
  p: SearchProductsParams
): Promise<SearchProductsResult> => {
  const from = (p.page - 1) * p.pageSize;
  const result: any = await API.graphql(
    graphqlOperation(searchProductLibProducts, {
      filter: buildProductFilter(p.brand, p.search),
      sort: [
        { field: p.sortField, direction: p.sortDirection },
        { field: "id", direction: p.sortDirection },
      ],
      limit: p.pageSize,
      from,
    })
  );

  const conn = result?.data?.searchProductLibProducts;
  return { items: conn?.items ?? [], total: conn?.total ?? 0 };
};

// Looks up a product by exact material code (case-insensitive). Returns null when
// no product with that matcode exists — used to validate batch-upload folders.
export const getProductByMaterialCode = async (
  materialCode: string
): Promise<ProductLibProduct | null> => {
  const result: any = await API.graphql(
    graphqlOperation(searchProductLibProducts, {
      filter: { materialCode: { matchPhrase: materialCode } },
      limit: 5,
    })
  );
  const items: ProductLibProduct[] =
    result?.data?.searchProductLibProducts?.items ?? [];
  const target = materialCode.toUpperCase();
  return (
    items.find((p) => (p.materialCode ?? "").toUpperCase() === target) ?? null
  );
};

// Stamps packageCaseUpdatedAt = now on the EA product row of a matcode. Called
// after any PK/CS photo action (upload / update / delete) so the product record
// tracks when its photos last changed. EA only. Best-effort (errors swallowed).
export const markPackageCaseUpdated = async (
  materialCode: string
): Promise<void> => {
  try {
    const result: any = await API.graphql(
      graphqlOperation(searchProductLibProducts, {
        filter: {
          and: [
            { materialCode: { matchPhrase: materialCode } },
            { packaging: { match: "EA" } },
          ],
        },
        limit: 50,
      })
    );
    const items: ProductLibProduct[] =
      result?.data?.searchProductLibProducts?.items ?? [];
    const target = materialCode.toUpperCase();
    const now = new Date().toISOString(); // AWSDateTime
    await Promise.all(
      items
        .filter(
          (p) =>
            (p.materialCode ?? "").toUpperCase() === target &&
            (p.packaging ?? "").toUpperCase() === "EA"
        )
        .map((p) =>
          API.graphql(
            graphqlOperation(updateProductLibProduct, {
              input: { id: p.id, packageCaseUpdatedAt: now },
            })
          )
        )
    );
  } catch (err) {
    console.log("markPackageCaseUpdated error:", materialCode, err);
  }
};
